/* eslint-disable eslint-comments/disable-enable-pair */

/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { useWorkplaceModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { CounterText, LoadingSpinner, Text, Title } from "@src/appV2/redesign/system/ui-components";
import { WorkplaceModalContent } from "@src/appV2/redesign/Workplace/Modal/Content";
import { WorkplaceModalFloatingFooter } from "@src/appV2/redesign/Workplace/Modal/FloatingFooter";
import {
  type WorkplaceLocationState,
  type WorkplaceProfile,
} from "@src/appV2/redesign/Workplace/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { minutesToMilliseconds } from "date-fns";
import { useRef } from "react";
import { InView } from "react-intersection-observer";
import { generatePath, useLocation } from "react-router-dom";

import { useSearchParams } from "../../../lib/utils/useSearchParams";
import { TextButton } from "../../components/TextButton";
import { useCredentialRequirementConfiguration } from "../../Documents/useCredentialRequirementConfiguration";
import { augmentYearsOfExperienceWithWorkerLicenses } from "../../PlacementCandidate/utils";
import { transformDiscoveryPlacementToData } from "../api/transformDiscoveryPlacementToData";
import { useFetchDiscoveryPlacements } from "../api/useFetchDiscoveryPlacements";
import {
  transformResponseIntoPlacement,
  useFetchPaginatedPlacements,
} from "../api/useFetchPaginatedPlacements";
import { PlacementCard } from "../components/PlacementCard";
import { PlacementsLoader } from "../components/PlacementsLoader";
import { usePayTransparencyRates } from "../lib/usePayTransparencyRates";
import {
  PLACEMENT_PATHS,
  PLACEMENT_WORKPLACE_PROFILE_PATH,
  WorkplacePlacementsPathSource,
} from "../paths";
import { usePlacementsFilter } from "../usePlacementsFilter";
import { placementWithDetailsToData } from "../utils/placement-data";

interface WorkplacePlacementsProps {
  workplaceId: string;
  workplace?: WorkplaceProfile;
  placementCandidate: PlacementCandidate;
}

export function WorkplacePlacements(props: WorkplacePlacementsProps) {
  const { workplaceId, workplace, placementCandidate } = props;

  const location = useLocation<WorkplaceLocationState>();

  const searchParams = useSearchParams();
  const source = searchParams.source as WorkplacePlacementsPathSource;

  const { generateModalPath } = useWorkplaceModalsDataContext();
  const worker = useDefinedWorker();

  const yearsOfExperienceByQualification = augmentYearsOfExperienceWithWorkerLicenses(
    placementCandidate.yearsOfExperienceByQualification,
    worker.licenses
  );

  const [placementsFilter] = usePlacementsFilter();

  const isDiscoveryMigrationEnabled = useCbhFlag(CbhFeatureFlag.PLACEMENTS_DISCOVERY_MIGRATION, {
    defaultValue: false,
  });

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  // Scope only by workplaceId — the workplace-scoped view should show every
  // job at this workplace. Matches the filter shape used by
  // useWorkplaceStaffJobsData, so the "View N staff jobs" button count and
  // the destination list share a React Query cache entry and tapping
  // through is a cache hit.
  const placementsFetchFilter = { workplaceId };

  // 5-minute staleTime mirrors useWorkplaceStaffJobsData so the cached
  // response stays fresh across the button → destination navigation.
  const staleTime = minutesToMilliseconds(5);

  const legacyResults = useFetchPaginatedPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id ?? "",
      filter: placementsFetchFilter,
      shouldLogEvent: true,
    },
    { enabled: isDefined(worker.userId) && !isDiscoveryMigrationEnabled, staleTime }
  );

  const discoveryResults = useFetchDiscoveryPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id ?? "",
      filter: placementsFetchFilter,
    },
    { enabled: isDefined(worker.userId) && isDiscoveryMigrationEnabled, staleTime }
  );

  const {
    isLoading: isPlacementsLoading,
    isSuccess: isPlacementsSuccess,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = isDiscoveryMigrationEnabled ? discoveryResults : legacyResults;

  const placementsData = isDiscoveryMigrationEnabled ? discoveryResults.data : legacyResults.data;

  const placementsCount = placementsData?.pages.reduce(
    (count, page) => count + page.data.length,
    0
  );

  const { getCachedPayRatesForPlacement, isLoading: isPayDataLoading } = usePayTransparencyRates();

  const scrollRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <StickyScrollPageHeader
        title={workplace?.name ?? ""}
        isLoading={isPlacementsLoading}
        scrollContainerRef={scrollRef}
        headerVariant="tertiary"
      />

      <WorkplaceModalContent ref={scrollRef} sx={{ display: "flex", flexDirection: "column" }}>
        <Stack spacing={6} pt={6}>
          {placementsCount ? (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <CounterText count={placementsCount}>
                <Title
                  bold
                  variant="h5"
                  component="h4"
                  sx={(theme) => ({ fontWeight: theme.typography.fontWeightMedium })}
                >
                  Jobs
                </Title>
              </CounterText>
              {source === "flex-workplace-profile-modal" && (
                <TextButton
                  href={generatePath(PLACEMENT_PATHS.ROOT)}
                  onClick={() => {
                    logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
                      placementCandidateId: placementCandidate.id,
                      filters: {
                        ...placementsFilter,
                        distanceInMiles: placementsFilter?.distanceInMiles?.$lt,
                      },
                      action: "explore_all_jobs_clicked",
                    });
                  }}
                >
                  Explore all jobs
                </TextButton>
              )}
            </Stack>
          ) : undefined}

          <Stack spacing={6}>
            {isPlacementsLoading ? <PlacementsLoader /> : undefined}

            {isPlacementsSuccess && (placementsData?.pages[0]?.data.length ?? 0) === 0 ? (
              <Stack justifyContent="center" flex={1} alignItems="center">
                <Text variant="body1">No relevant jobs listed yet!</Text>
              </Stack>
            ) : undefined}

            {isDiscoveryMigrationEnabled &&
            isDefined(discoveryResults.data) &&
            discoveryResults.data.pages.length > 0 ? (
              <Stack direction="column" spacing={6}>
                {discoveryResults.data.pages.flatMap((placementPage, pageIndex) => {
                  return placementPage.data.map((placement, placementIndex) => {
                    const placementData = transformDiscoveryPlacementToData(
                      placement,
                      placementPage.included,
                      credentialRequirementConfiguration
                    );

                    if (!isDefined(placementData)) {
                      return undefined;
                    }

                    const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
                      {
                        id: placementData.id,
                        workplaceId: placementData.workplace.id,
                        workerTypes: placementData.workerTypes,
                        jobShifts: placementData.jobShifts,
                        transparencyPayRates: placementData.transparencyPayRates,
                        attributes: { payRange: placementData.payRange },
                      },
                      yearsOfExperienceByQualification,
                      false,
                      placementCandidate.payAndScheduleFormData?.benefits?.combined
                    );

                    return (
                      <PlacementCard
                        key={placementData.id}
                        placement={placementData}
                        placementCandidateId={placementCandidate.id}
                        isRestricted={placementCandidate.isRestricted ?? false}
                        pageIndex={pageIndex}
                        placementIndex={placementIndex}
                        minMaxPayEstimate={minMaxPayEstimate}
                        loadingPayData={isPayDataLoading}
                        showWorkplaceRatings={false}
                        source="workplace_placement_card"
                      />
                    );
                  });
                })}
              </Stack>
            ) : undefined}

            {!isDiscoveryMigrationEnabled &&
            isDefined(legacyResults.data) &&
            legacyResults.data.pages.length > 0 ? (
              <Stack direction="column" spacing={6}>
                {legacyResults.data.pages.flatMap((placementPage, pageIndex) => {
                  return placementPage.data.map((placement, placementIndex) => {
                    const placementWithDetails = transformResponseIntoPlacement(
                      placement,
                      placementPage.included,
                      credentialRequirementConfiguration
                    );

                    const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
                      {
                        id: placementWithDetails.id,
                        workplaceId: placementWithDetails.workplace.id,
                        workerTypes: placementWithDetails.workerTypes,
                        jobShifts: placementWithDetails.jobShifts,
                        transparencyPayRates: placementWithDetails.transparencyPayRates,
                        attributes: { payRange: placementWithDetails.payRange },
                      },
                      yearsOfExperienceByQualification,
                      false,
                      placementCandidate.payAndScheduleFormData?.benefits?.combined
                    );

                    return (
                      <PlacementCard
                        key={placementWithDetails.id}
                        placement={placementWithDetailsToData(
                          placementWithDetails,
                          credentialRequirementConfiguration
                        )}
                        placementCandidateId={placementCandidate.id}
                        isRestricted={placementCandidate.isRestricted ?? false}
                        pageIndex={pageIndex}
                        placementIndex={placementIndex}
                        minMaxPayEstimate={minMaxPayEstimate}
                        loadingPayData={isPayDataLoading}
                        showWorkplaceRatings={false}
                        source="workplace_placement_card"
                      />
                    );
                  });
                })}
              </Stack>
            ) : undefined}

            {hasNextPage ? (
              <Stack direction="row" justifyContent="center">
                <Box sx={{ marginY: 4 }}>
                  <InView
                    as="div"
                    onChange={async (inView) => {
                      if (inView && !isFetchingNextPage) {
                        await fetchNextPage();
                        logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
                          placementCandidateId: placementCandidate.id,
                          filters: {
                            ...placementsFilter,
                            distanceInMiles: placementsFilter?.distanceInMiles?.$lt,
                          },
                          action: "load_more_clicked",
                        });
                      }
                    }}
                  />
                  <Box sx={{ opacity: isFetchingNextPage ? 1 : 0 }}>
                    <LoadingSpinner size="medium" />
                  </Box>
                </Box>
              </Stack>
            ) : undefined}
          </Stack>
        </Stack>
      </WorkplaceModalContent>
      <WorkplaceModalFloatingFooter>
        {location.state?.origin !== "WORKPLACE_PROFILE_MODAL" && (
          <Button
            fullWidth
            variant="contained"
            href={generateModalPath(PLACEMENT_WORKPLACE_PROFILE_PATH, {
              workplaceId,
            })}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.VIEWED_FACILITY_PROFILE, {
                workplaceId,
                trigger: "Placements Workplace Jobs Page",
              });
            }}
          >
            View profile
          </Button>
        )}
      </WorkplaceModalFloatingFooter>
    </>
  );
}
